import { Box, InputBaseComponentProps } from '@mui/material';
import React, { forwardRef, useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Parses an inputFormat string (e.g. "dd/MM/yyyy") into segments.
 */
interface FormatSegment {
  type: 'placeholder' | 'separator';
  text: string;
  start: number;
  end: number;
}

const DATE_TIME_TOKEN_CHARS = new Set(['d', 'M', 'y', 'H', 'h', 'm', 's', 'a']);

function parseFormat(inputFormat: string): FormatSegment[] {
  const segments: FormatSegment[] = [];
  const placeholderChars = DATE_TIME_TOKEN_CHARS;
  let i = 0;

  while (i < inputFormat.length) {
    const ch = inputFormat[i];
    if (placeholderChars.has(ch)) {
      const start = i;
      while (i < inputFormat.length && inputFormat[i] === ch) {
        i++;
      }
      segments.push({ type: 'placeholder', text: inputFormat.slice(start, i), start, end: i });
    } else {
      segments.push({ type: 'separator', text: ch, start: i, end: i + 1 });
      i++;
    }
  }

  return segments;
}

function isDigit(ch: string): boolean {
  return ch >= '0' && ch <= '9';
}

function buildDisplayValue(digits: string, segments: FormatSegment[]): string {
  let displayValue = '';
  let digitIndex = 0;

  for (const segment of segments) {
    if (segment.type === 'separator') {
      displayValue += segment.text;
    } else {
      for (let j = 0; j < segment.text.length; j++) {
        if (digitIndex < digits.length) {
          displayValue += digits[digitIndex];
          digitIndex++;
        } else {
          displayValue += segment.text[j];
        }
      }
    }
  }

  return displayValue;
}

function getMaxDigits(segments: FormatSegment[]): number {
  return segments.reduce(
    (sum, seg) => (seg.type === 'placeholder' ? sum + seg.text.length : sum),
    0,
  );
}

function getCursorPositionForDigitCount(digitCount: number, segments: FormatSegment[]): number {
  if (digitCount === 0) return 0;

  let remaining = digitCount;
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i];
    if (segment.type === 'separator') {
      continue;
    }
    if (remaining < segment.text.length) {
      // Cursor is within this group
      return segment.start + remaining;
    }
    if (remaining === segment.text.length) {
      // This group is exactly filled. Skip any following separators
      // to place the cursor at the start of the next placeholder group.
      let nextPos = segment.end;
      for (let j = i + 1; j < segments.length; j++) {
        if (segments[j].type === 'separator') {
          nextPos = segments[j].end;
        } else {
          break;
        }
      }
      return nextPos;
    }
    remaining -= segment.text.length;
  }
  // All digits filled, position at end
  const lastSeg = segments[segments.length - 1];
  return lastSeg ? lastSeg.end : 0;
}

function getDigitIndexAtPosition(position: number, segments: FormatSegment[]): number {
  let digitIndex = 0;
  for (const segment of segments) {
    if (segment.type === 'separator') {
      if (position <= segment.start) return digitIndex;
      continue;
    }
    for (let j = 0; j < segment.text.length; j++) {
      const charPos = segment.start + j;
      if (position <= charPos) return digitIndex;
      digitIndex++;
    }
  }
  return digitIndex;
}

/**
 * Computes `filledLength`: the index in the display string up to which
 * characters are user-typed (including any separators between completed groups).
 *
 * When a placeholder group is fully completed, trailing separators up to the
 * next placeholder group are also considered "filled" so they render in the
 * normal text colour (e.g. "21/" rather than "21" + faded "/").
 */
function getFilledLength(digits: string, segments: FormatSegment[]): number {
  if (digits.length === 0) return 0;

  let digitIndex = 0;
  let filledEnd = 0;

  for (let segIdx = 0; segIdx < segments.length; segIdx++) {
    const segment = segments[segIdx];

    if (segment.type === 'separator') {
      // A separator is "filled" if the preceding placeholder group was fully
      // completed AND there are still digits to fill.
      // We handle this in the placeholder branch below.
      continue;
    }

    // Placeholder segment
    let groupFilled = 0;
    for (let j = 0; j < segment.text.length; j++) {
      if (digitIndex < digits.length) {
        filledEnd = segment.start + j + 1;
        digitIndex++;
        groupFilled++;
      }
    }

    // If this entire group was filled, include any immediately following separators
    // in the filled area so they render in normal colour.
    if (groupFilled === segment.text.length) {
      for (let nextIdx = segIdx + 1; nextIdx < segments.length; nextIdx++) {
        if (segments[nextIdx].type === 'separator') {
          filledEnd = segments[nextIdx].end;
        } else {
          break;
        }
      }
    }
  }

  return filledEnd;
}

interface MaskedDateInputProps extends InputBaseComponentProps {
  inputFormat?: string;
  onMaskedChange?: (formattedValue: string, isComplete: boolean) => void;
}

/**
 * A custom input component for MUI TextField that provides guided date masking.
 *
 * As users type digits, the input format pattern remains visible:
 * - Typed characters appear in normal text color
 * - Untyped format characters appear in placeholder color
 * - Cursor auto-advances past separators
 * - Only fully matching input is considered valid
 */
const MaskedDateInput = forwardRef<HTMLInputElement, MaskedDateInputProps>(
  function MaskedDateInput(props, forwardedRef) {
    const {
      inputFormat = 'dd/MM/yyyy',
      onMaskedChange,
      value: externalValue,
      onChange: externalOnChange,
      onFocus: externalOnFocus,
      onBlur: externalOnBlur,
      ...cleanProps
    } = props;

    const segments = useMemo(() => parseFormat(inputFormat), [inputFormat]);
    const maxDigits = useMemo(() => getMaxDigits(segments), [segments]);

    const [digits, setDigits] = useState<string>('');
    const [isFocused, setIsFocused] = useState(false);
    const internalRef = useRef<HTMLInputElement | null>(null);
    const pendingCursorPos = useRef<number | null>(null);

    // Combine refs
    const setRef = useCallback(
      (node: HTMLInputElement | null) => {
        internalRef.current = node;
        if (typeof forwardedRef === 'function') {
          forwardedRef(node);
        } else if (forwardedRef) {
          (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
        }
      },
      [forwardedRef],
    );

    // Sync from external value changes (e.g., calendar picker)
    useEffect(() => {
      if (typeof externalValue === 'string' && externalValue) {
        const externalDigits = externalValue.replace(/\D/g, '');
        if (externalDigits.length === maxDigits) {
          setDigits(externalDigits);
        }
      } else if (externalValue === '' || externalValue === null || externalValue === undefined) {
        // If external value is cleared, clear digits
        if (!isFocused) {
          setDigits('');
        }
      }
    }, [externalValue, maxDigits, isFocused]);

    const displayValue = isFocused || digits.length > 0 ? buildDisplayValue(digits, segments) : '';

    const filledLength = getFilledLength(digits, segments);

    // Apply pending cursor position after render
    useEffect(() => {
      if (pendingCursorPos.current !== null && internalRef.current) {
        const pos = pendingCursorPos.current;
        pendingCursorPos.current = null;
        requestAnimationFrame(() => {
          if (internalRef.current) {
            internalRef.current.setSelectionRange(pos, pos);
          }
        });
      }
    });

    const setCursorAfterDigits = useCallback(
      (newDigitCount: number) => {
        pendingCursorPos.current = getCursorPositionForDigitCount(newDigitCount, segments);
      },
      [segments],
    );

    const fireChange = useCallback(
      (newDigits: string) => {
        const newDisplayValue = buildDisplayValue(newDigits, segments);
        const isComplete = newDigits.length === maxDigits;

        // Fire the external onChange so MUI DatePicker can parse the value
        if (externalOnChange && internalRef.current) {
          // Provide the correct reporting value to MUI
          // We only report the value if it's complete, otherwise report empty
          const reportValue = isComplete ? newDisplayValue : '';

          // Temporarily set the DOM value for the benefit of the onChange event
          // This ensures MUI/RHF sees the validation state we want
          const actualDomValue = internalRef.current.value;
          internalRef.current.value = reportValue;

          const event = {
            target: internalRef.current,
            currentTarget: internalRef.current,
            persist: () => {},
            type: 'change',
          } as any;
          externalOnChange(event);

          // Restore the display value so the user still sees the mask/digits correctly
          internalRef.current.value = actualDomValue;
        }

        onMaskedChange?.(newDisplayValue, isComplete);
      },
      [maxDigits, segments, externalOnChange, onMaskedChange],
    );

    const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
      // We handle all input via onKeyDown and onPaste, so suppress default onChange
      e.preventDefault();
    }, []);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLInputElement>) => {
        const input = e.currentTarget;

        if (e.key === 'Backspace') {
          e.preventDefault();
          if (digits.length > 0) {
            const selStart = input.selectionStart ?? digits.length;
            const selEnd = input.selectionEnd ?? selStart;

            if (selStart !== selEnd) {
              const startDigitIdx = getDigitIndexAtPosition(selStart, segments);
              const endDigitIdx = getDigitIndexAtPosition(selEnd, segments);
              const newDigits = digits.slice(0, startDigitIdx) + digits.slice(endDigitIdx);
              setDigits(newDigits);
              setCursorAfterDigits(startDigitIdx);
              fireChange(newDigits);
            } else {
              const digitIdx = getDigitIndexAtPosition(selStart, segments);
              if (digitIdx > 0) {
                const deleteIdx = digitIdx - 1;
                const newDigits = digits.slice(0, deleteIdx) + digits.slice(deleteIdx + 1);
                setDigits(newDigits);
                setCursorAfterDigits(deleteIdx);
                fireChange(newDigits);
              }
            }
          }
          return;
        }

        if (e.key === 'Delete') {
          e.preventDefault();
          const selStart = input.selectionStart ?? 0;
          const selEnd = input.selectionEnd ?? selStart;

          if (selStart !== selEnd) {
            const startDigitIdx = getDigitIndexAtPosition(selStart, segments);
            const endDigitIdx = getDigitIndexAtPosition(selEnd, segments);
            const newDigits = digits.slice(0, startDigitIdx) + digits.slice(endDigitIdx);
            setDigits(newDigits);
            setCursorAfterDigits(startDigitIdx);
            fireChange(newDigits);
          } else {
            const digitIdx = getDigitIndexAtPosition(selStart, segments);
            if (digitIdx < digits.length) {
              const newDigits = digits.slice(0, digitIdx) + digits.slice(digitIdx + 1);
              setDigits(newDigits);
              setCursorAfterDigits(digitIdx);
              fireChange(newDigits);
            }
          }
          return;
        }

        // Allow navigation keys
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End', 'Tab', 'Escape', 'Enter'].includes(e.key)) {
          return;
        }

        // Allow Ctrl/Cmd shortcuts
        if (e.ctrlKey || e.metaKey) {
          return;
        }

        // Digit input
        if (isDigit(e.key)) {
          e.preventDefault();
          if (digits.length < maxDigits) {
            const selStart = input.selectionStart ?? digits.length;
            const selEnd = input.selectionEnd ?? selStart;
            const startDigitIdx = getDigitIndexAtPosition(selStart, segments);
            const endDigitIdx = getDigitIndexAtPosition(selEnd, segments);

            let newDigits: string;
            if (selStart !== selEnd) {
              newDigits = digits.slice(0, startDigitIdx) + e.key + digits.slice(endDigitIdx);
            } else {
              newDigits = digits.slice(0, startDigitIdx) + e.key + digits.slice(startDigitIdx);
            }

            if (newDigits.length > maxDigits) {
              newDigits = newDigits.slice(0, maxDigits);
            }

            setDigits(newDigits);
            const newCursorDigitIdx = Math.min(startDigitIdx + 1, newDigits.length);
            setCursorAfterDigits(newCursorDigitIdx);
            fireChange(newDigits);
          }
          return;
        }

        // Block all other input
        e.preventDefault();
      },
      [digits, maxDigits, segments, setCursorAfterDigits, fireChange],
    );

    const handleClick = useCallback(
      (e: React.MouseEvent<HTMLInputElement>) => {
        const input = e.currentTarget;
        // Strict cursor enforcement: don't allow cursor past typed digits
        requestAnimationFrame(() => {
          if (input) {
            const pos = input.selectionStart ?? 0;
            const maxPos = getCursorPositionForDigitCount(digits.length, segments);
            if (pos > maxPos) {
              input.setSelectionRange(maxPos, maxPos);
            }
          }
        });
      },
      [digits.length, segments],
    );

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true);
        setCursorAfterDigits(digits.length);
        externalOnFocus?.(e);
      },
      [digits.length, setCursorAfterDigits, externalOnFocus],
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false);
        externalOnBlur?.(e);
      },
      [externalOnBlur],
    );

    const handlePaste = useCallback(
      (e: React.ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const pastedText = e.clipboardData.getData('text');
        const pastedDigits = pastedText.replace(/\D/g, '');

        if (pastedDigits.length > 0) {
          const input = e.currentTarget;
          const selStart = input.selectionStart ?? digits.length;
          const selEnd = input.selectionEnd ?? selStart;
          const startDigitIdx = getDigitIndexAtPosition(selStart, segments);
          const endDigitIdx = getDigitIndexAtPosition(selEnd, segments);

          let newDigits = digits.slice(0, startDigitIdx) + pastedDigits + digits.slice(endDigitIdx);
          if (newDigits.length > maxDigits) {
            newDigits = newDigits.slice(0, maxDigits);
          }

          setDigits(newDigits);
          setCursorAfterDigits(Math.min(startDigitIdx + pastedDigits.length, newDigits.length));
          fireChange(newDigits);
        }
      },
      [digits, maxDigits, segments, setCursorAfterDigits, fireChange],
    );

    // Build the overlay content: typed characters in normal color, rest in placeholder color
    const renderOverlay = () => {
      if (!displayValue) return null;

      // The overlay container uses the same MUI className to get identical padding/font settings
      return (
        <Box
          aria-hidden="true"
          className={props.className}
          sx={{
            ...props.style, // Restoring styles needed for alignment
            gridArea: '1 / 1',
            position: 'relative',
            pointerEvents: 'none',
            whiteSpace: 'pre',
            background: 'transparent',
            border: 'none',
            display: 'flex',
            alignItems: 'center',
            boxSizing: 'inherit',
            margin: 0,
            zIndex: 0,
            color: 'inherit',
            WebkitTextFillColor: 'initial',
            height: '100%',
          }}
        >
          {displayValue.split('').map((ch, idx) => {
            const isFilled = idx < filledLength && digits.length > 0;
            // Check if this position in the original format is a separator
            const formatChar = inputFormat[idx];
            const isSeparator = formatChar && !DATE_TIME_TOKEN_CHARS.has(formatChar);
            // Separators before filled area should be normal color too
            const isFilledSeparator = isSeparator && idx < filledLength && digits.length > 0;

            return (
              <Box
                key={idx}
                component="span"
                sx={{
                  color: 'inherit',
                  opacity: isFilled || isFilledSeparator ? 1 : 0.5,
                }}
              >
                {ch}
              </Box>
            );
          })}
        </Box>
      );
    };

    return (
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          height: '100%',
          position: 'relative',
        }}
        onClick={() => internalRef.current?.focus()}
      >
        <Box
          sx={{
            display: 'grid',
            width: '100%',
            height: '100%',
            gridTemplateColumns: 'minmax(0, 1fr)',
            alignItems: 'center',
          }}
        >
          <Box
            component="input"
            {...cleanProps}
            ref={setRef}
            sx={{
              ...props.style,
              gridArea: '1 / 1',
              WebkitTextFillColor: 'transparent',
              caretColor: 'currentColor',
              background: 'transparent',
              border: 'none',
              outline: 'none',
              zIndex: 1,
              height: '100%',
              width: '100%',
              padding: 0, // Padding is handled by the parent TextField wrapper
              font: 'inherit',
              color: 'inherit',
            }}
            value={displayValue}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onClick={handleClick}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onPaste={handlePaste}
          />
          {renderOverlay()}
        </Box>
      </Box>
    );
  },
);

export default MaskedDateInput;
